// Hardcoded comparison content. Each entry powers a public /comparaisons/:slug
// page AND its prerendered SEO snapshot in scripts/prerender-seo.mjs. Keep both
// in sync when adding new comparisons.

export interface ComparisonRow {
  feature: string;
  thiia: string;
  competitor: string;
  thiiaWins?: boolean;
}

export interface ComparisonContent {
  slug: string;
  competitor: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  intro: string;
  positioning: string;
  rows: ComparisonRow[];
  whenToChoose: { thiia: string[]; competitor: string[] };
  faq: { question: string; answer: string }[];
}

export const COMPARISONS: ComparisonContent[] = [
  {
    slug: "thiia-vs-hubspot",
    competitor: "HubSpot",
    title: "Thiia vs HubSpot",
    metaTitle: "Thiia vs HubSpot — Alternative française et souveraine au CRM américain",
    metaDescription:
      "Comparatif détaillé Thiia vs HubSpot : agent IA proactif, souveraineté française, hébergement européen, conformité RGPD native. Pour qui chaque solution est faite ?",
    h1: "Thiia vs HubSpot : l'alternative française et souveraine",
    intro:
      "HubSpot est une plateforme CRM américaine complète mais lourde, pensée pour les équipes commerciales structurées. Thiia est un agent IA souverain conçu pour les coachs, indépendants et petites équipes francophones qui veulent un outil de prospection intelligent, conforme RGPD nativement, sans usine à gaz.",
    positioning:
      "HubSpot répond à la question « comment organiser mon équipe commerciale ? ». Thiia répond à la question « comment trouver et qualifier mes prospects pendant que je travaille ? ». Les deux sont compatibles : Thiia se synchronise avec HubSpot via OAuth bi-directionnel.",
    rows: [
      { feature: "Souveraineté", thiia: "Française (Mistral) + hébergement UE", competitor: "Américaine, hébergement US par défaut", thiiaWins: true },
      { feature: "Agent IA proactif", thiia: "Recherche, qualifie, rédige, relance", competitor: "Assistant IA en option (payante)", thiiaWins: true },
      { feature: "RGPD natif", thiia: "Consentement obligatoire, désinscription auto", competitor: "Conforme mais à configurer", thiiaWins: true },
      { feature: "Recherche web de prospects", thiia: "Perplexity intégré", competitor: "Non inclus (modules externes)", thiiaWins: true },
      { feature: "Tarification", thiia: "Freemium puis achat de crédits", competitor: "Hubs séparés, tarifs croissants" },
      { feature: "Adapté grandes équipes", thiia: "Petites équipes / indépendants", competitor: "Équipes de 10+ commerciaux" },
      { feature: "Marketplace d'extensions", thiia: "Intégrations clés (Gmail, Outlook, HubSpot, Stripe…)", competitor: "1500+ apps" },
      { feature: "Support en français", thiia: "Oui, équipe française (KREAPP)", competitor: "Multilingue, support international" },
      { feature: "Synchronisation HubSpot", thiia: "Oui, OAuth bi-directionnel", competitor: "—" },
    ],
    whenToChoose: {
      thiia: [
        "Vous êtes coach, freelance, indépendant ou petite équipe (< 10).",
        "La conformité RGPD et la souveraineté européenne sont prioritaires.",
        "Vous voulez un agent qui prospecte, pas juste une base de fiches.",
        "Vous préférez un seul outil intégré à 5 abonnements.",
      ],
      competitor: [
        "Vous gérez une équipe commerciale structurée de 10+ personnes.",
        "Vous avez besoin de marketing automation avancé (HubSpot Marketing Hub).",
        "Vous utilisez déjà l'écosystème HubSpot (Service, CMS, Ops).",
        "Vous avez un budget CRM > 500 €/mois.",
      ],
    },
    faq: [
      {
        question: "Thiia remplace-t-il HubSpot ?",
        answer:
          "Pour les indépendants et petites équipes, oui — Thiia couvre CRM, emails, recherche, calendrier et séquences en une seule application. Pour les équipes commerciales structurées de 10+ personnes, Thiia se synchronise avec HubSpot plutôt que de le remplacer, via OAuth bi-directionnel des contacts et lifecyclestage.",
      },
      {
        question: "Mes données sont-elles transférées aux États-Unis ?",
        answer:
          "Non avec Thiia : hébergement Supabase région Europe (Francfort), IA principale Mistral (souveraine française). HubSpot stocke ses données par défaut aux États-Unis avec option Data Residency UE payante.",
      },
      {
        question: "Combien coûte Thiia par rapport à HubSpot ?",
        answer:
          "Thiia propose un modèle freemium puis un achat de crédits par module, là où HubSpot facture en hubs séparés (Sales, Marketing, Service) avec des paliers utilisateurs et des add-ons. Pour un indépendant, Thiia est typiquement 3 à 10× moins cher que l'équivalent HubSpot Sales Pro.",
      },
      {
        question: "Puis-je migrer mes contacts HubSpot vers Thiia ?",
        answer:
          "Oui, via la synchronisation OAuth bi-directionnelle ou par import CSV. Les fiches, organisations et propriétés personnalisées sont préservées.",
      },
    ],
  },
  {
    slug: "thiia-vs-pipedrive",
    competitor: "Pipedrive",
    title: "Thiia vs Pipedrive",
    metaTitle: "Thiia vs Pipedrive — Agent IA souverain ou pipeline kanban classique ?",
    metaDescription:
      "Comparatif Thiia vs Pipedrive : agent IA proactif souverain (Mistral, RGPD) versus pipeline kanban estonien. Pour qui chaque solution est-elle adaptée ?",
    h1: "Thiia vs Pipedrive : agent IA proactif ou pipeline classique ?",
    intro:
      "Pipedrive est un CRM kanban estonien apprécié pour sa simplicité de pipeline visuel. Thiia est un agent IA souverain qui ne se contente pas d'afficher un pipeline : il recherche les prospects, les qualifie, rédige les emails et programme les relances pour vous.",
    positioning:
      "Pipedrive est un excellent gestionnaire de pipeline si vous avez déjà votre flux de prospects. Thiia est un agent qui produit ce flux : recherche web automatique, qualification IA, génération d'emails personnalisés. Le pipeline kanban est inclus dans les deux outils.",
    rows: [
      { feature: "Agent IA proactif", thiia: "Recherche, qualifie, rédige", competitor: "Assistant IA basique", thiiaWins: true },
      { feature: "Recherche web de prospects", thiia: "Perplexity intégré, veille auto quotidienne", competitor: "Non inclus", thiiaWins: true },
      { feature: "Souveraineté", thiia: "Française (KREAPP, Mistral, UE)", competitor: "Estonienne, hébergement US/UE", thiiaWins: true },
      { feature: "RGPD natif", thiia: "Consentement obligatoire, désinscription auto", competitor: "Conforme, paramétrable" },
      { feature: "Pipeline kanban", thiia: "Oui, avec clusters relationnels", competitor: "Oui, référence du marché" },
      { feature: "Emails IA personnalisés", thiia: "Gemini + Mistral, multilingue auto", competitor: "Templates + IA en add-on", thiiaWins: true },
      { feature: "Calendrier intégré", thiia: "Google, Outlook, Teams", competitor: "Sync calendrier" },
      { feature: "Pages de réservation", thiia: "Incluses (créneaux dynamiques)", competitor: "Add-on Scheduler payant", thiiaWins: true },
      { feature: "Tarification", thiia: "Freemium puis achat de crédits", competitor: "Paliers Essential → Enterprise" },
    ],
    whenToChoose: {
      thiia: [
        "Vous voulez que l'outil produise vos prospects, pas juste les afficher.",
        "Vous êtes coach, freelance ou consultant francophone.",
        "La souveraineté française et le RGPD natif comptent.",
        "Vous facturez par rendez-vous (les pages de réservation sont incluses).",
      ],
      competitor: [
        "Vous avez déjà un flux de prospects abondant à organiser.",
        "Votre équipe utilise Pipedrive depuis longtemps (changement coûteux).",
        "Vous n'avez pas besoin de génération IA d'emails ni de recherche web.",
      ],
    },
    faq: [
      {
        question: "Thiia est-il un CRM ou un agent ?",
        answer:
          "Les deux. Thiia inclut un CRM kanban équivalent à Pipedrive (pipeline visuel, statuts, relations) ET un agent IA qui agit dessus de manière proactive (recherche, qualification, rédaction, relance).",
      },
      {
        question: "Pipedrive a aussi de l'IA, quelle différence ?",
        answer:
          "L'IA de Pipedrive (Sales Assistant) suggère des actions et résume des conversations. L'IA de Thiia exécute : elle recherche réellement de nouveaux prospects via Perplexity, rédige les emails complets en français ou anglais, et déclenche les relances multi-étapes avec arrêt automatique sur réponse.",
      },
      {
        question: "Mes données restent-elles en Europe avec Thiia ?",
        answer:
          "Oui : hébergement Supabase Francfort, IA principale Mistral (souveraine française), Gemini et Perplexity en mode zéro-entraînement. Aucun transfert hors UE par défaut.",
      },
    ],
  },
  {
    slug: "agent-ia-prospection-souverain",
    competitor: "Outils américains",
    title: "Agent IA de prospection souverain",
    metaTitle: "Agent IA de prospection souverain — Pourquoi choisir une solution française ?",
    metaDescription:
      "Pourquoi choisir un agent IA de prospection souverain et français en 2026 ? Souveraineté Mistral, hébergement UE, conformité RGPD, AIIA, AI Act. Comparaison avec les outils américains.",
    h1: "Pourquoi choisir un agent IA de prospection souverain ?",
    intro:
      "En 2026, la majorité des outils de prospection IA sont américains : leurs modèles, leur hébergement, leur juridiction. Choisir un agent souverain comme Thiia, c'est garder la maîtrise de vos données commerciales, anticiper l'AI Act européen et offrir une garantie crédible à vos prospects européens.",
    positioning:
      "« Souverain » ne veut pas dire « moins puissant ». Mistral rivalise avec GPT-4 sur la rédaction email en français. Supabase région UE offre les mêmes garanties techniques qu'AWS US. Thiia combine ces briques européennes dans un agent prêt à l'emploi.",
    rows: [
      { feature: "Modèle IA principal", thiia: "Mistral (français)", competitor: "GPT, Claude, Gemini (US)", thiiaWins: true },
      { feature: "Hébergement données", thiia: "UE (Francfort)", competitor: "US par défaut", thiiaWins: true },
      { feature: "Juridiction", thiia: "Droit français + RGPD", competitor: "Cloud Act US applicable" },
      { feature: "Garantie zéro-entraînement", thiia: "Contractuelle, multi-modèles", competitor: "Variable selon fournisseur" },
      { feature: "AIIA publique", thiia: "Oui, conforme CNIL", competitor: "Rare" },
      { feature: "Préparation AI Act", thiia: "Native (transparence, supervision)", competitor: "À auditer" },
      { feature: "Support en français", thiia: "Équipe française KREAPP", competitor: "Support international" },
      { feature: "Conformité RGPD native", thiia: "Consentement, désinscription, journal", competitor: "À configurer" },
    ],
    whenToChoose: {
      thiia: [
        "Vous prospectez en Europe et vos prospects vous demandent des garanties RGPD.",
        "Vous êtes dans un secteur sensible (santé, éducation, finance, secteur public).",
        "Vous voulez anticiper l'AI Act européen sans audit coûteux.",
        "Vous valorisez l'écosystème tech français et européen.",
      ],
      competitor: [
        "Vous prospectez exclusivement en marché américain.",
        "Aucune contrainte de souveraineté ou RGPD ne s'applique à vos prospects.",
      ],
    },
    faq: [
      {
        question: "Qu'est-ce qu'un agent IA souverain ?",
        answer:
          "Un agent IA souverain combine trois critères : un modèle IA dont l'éditeur est soumis au droit européen (Mistral), un hébergement de données en UE (Supabase Francfort) et un éditeur de la solution lui-même européen (KREAPP SAS, France). Cela garantit que ni le Cloud Act américain ni les lois extraterritoriales ne peuvent contraindre l'accès à vos données.",
      },
      {
        question: "Mistral est-il aussi performant que GPT-4 pour la prospection ?",
        answer:
          "Oui pour la rédaction d'emails en français et anglais, la qualification de prospects et l'analyse d'intention. Les benchmarks publics (Mistral Large 2, Mixtral 8x22B) le placent au niveau de GPT-4 Turbo sur les tâches métier non-codantes. Thiia utilise aussi Gemini et Perplexity pour des tâches spécifiques, tous configurés en zéro-entraînement.",
      },
      {
        question: "Le RGPD interdit-il vraiment d'utiliser les outils américains ?",
        answer:
          "Non, mais il impose des garanties contractuelles complexes (Clauses Contractuelles Types, analyses d'impact transfert). Choisir un outil souverain européen évite ces démarches et le risque juridique en cas d'invalidation des accords UE-US (cf. arrêts Schrems I et II).",
      },
      {
        question: "L'AI Act européen change-t-il la donne ?",
        answer:
          "Oui. À partir de 2026-2027, les systèmes IA utilisés en B2B en Europe devront documenter leur usage, leurs risques et leur supervision humaine. Thiia publie déjà son AIIA (Analyse d'Impact IA) et applique nativement les principes de transparence et d'intervention humaine exigés par le règlement.",
      },
    ],
  },
];

export function findComparison(slug: string): ComparisonContent | undefined {
  return COMPARISONS.find((c) => c.slug === slug);
}
